import { FC, useEffect } from 'react';
import { css } from '@emotion/react';
import { GetCallsRequest } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2';
import { Call } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';
import { CallIntelQueries, CallIntelTypes, CallIntelUtils } from '@frontend/api-call-intel';
import { CallInsightApi, useCallInsightController } from '@frontend/call-insight-panel';
import { EmptyStateConfig, InfinitePaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { Photos } from '@frontend/photos';
import { breakpoints } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Chip, formatDate, NakedButton, SkeletonLoaders, Text, Truncated } from '@frontend/design-system';
import { DISPLAY_DATE_FORMAT } from '../../constants';

interface CallListCardProps {
  request: GetCallsRequest;
  showLocationChip?: boolean;
  emptyState?: EmptyStateConfig;
  showDemo?: boolean;
}

export const CallListCard: FC<CallListCardProps> = ({ request, showLocationChip = false, emptyState, showDemo }) => {
  const { t } = useTranslation('callIntelDashboardWidgets');
  const queryResult = CallIntelQueries.useListCallsQuery({
    request,
    options: {
      keepPreviousData: true,
      enabled: true,
      staleTime: 5 * 60 * 1000,
      select: (data) => {
        return {
          pages: data.pages.map(({ calls }) => ({
            rows: calls as CallIntelTypes.Call[],
          })),
          pageParams: data.pageParams,
        };
      },
    },
  });

  const {
    open: openCallInsightPanel,
    setCallList,
    setIsDemoAccount,
  } = useCallInsightController({ source: 'dashboardUnhappyWidget' });

  useEffect(() => {
    // Update call list in takeaway store for navigation
    const allCalls = queryResult.data?.pages.flatMap((page) => page.rows) || [];
    const callListItems = allCalls.map(
      (call) =>
        ({
          channelId: call.id,
          locationId: call.locationId,
          includeCallIntelligence: false,
          callIntelData: call as Call,
        } as const)
    );
    setCallList(callListItems);
  }, [setCallList, queryResult.data]);

  useEffect(() => {
    setIsDemoAccount(!!showDemo);
  }, [setIsDemoAccount, showDemo]);

  return (
    <div css={styles.container}>
      <InfinitePaginatedList
        height={'100%'}
        emptyStateConfig={{
          header: t('No Calls'),
          type: 'no_calls_made',
          styles: { height: 300 },
          ...emptyState,
        }}
        endOfListText={''}
        infiniteQueryProps={queryResult}
        renderListItem={({ listItem }) => (
          <CallRecordItem
            key={listItem.id}
            call={listItem}
            openCallTakeawayPanel={openCallInsightPanel}
            showLocationChip={showLocationChip}
          />
        )}
        skeletonLoader={<CallListLoader />}
      />
    </div>
  );
};

const CallListLoader: FC = () => {
  return (
    <div css={[styles.callItem, { padding: theme.spacing(1) }]}>
      <SkeletonLoaders.Loader height={24} />
    </div>
  );
};

const CallRecordItem: FC<{
  call: CallIntelTypes.Call;
  showLocationChip: boolean;
  openCallTakeawayPanel: CallInsightApi['open'];
}> = ({ call, showLocationChip, openCallTakeawayPanel }) => {
  const { accessibleLocationData } = useAppScopeStore();
  const locationName = call.locationId ? accessibleLocationData[call.locationId]?.name : '';
  const { t } = useTranslation('callIntelDashboardWidgets');

  const firstName = call.person?.firstName?.trim();
  const lastName = call.person?.lastName?.trim();
  const phoneNumber = formatPhoneNumber(CallIntelUtils.getPhoneNumber(call.phoneNumber));
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  return (
    <div css={styles.callItem}>
      <Photos.ContactProfilePhoto
        size='small'
        personId={call.person?.id ?? ''}
        locationId={call.locationId}
        name={fullName || t('Unknown')}
      />
      <div css={styles.callItemContent}>
        <div css={styles.userInfoContent}>
          <div css={styles.userInfoTitle}>
            <Truncated as={Text} size='medium'>
              {!!fullName ? (
                <>
                  <b>{firstName}</b> {lastName}
                </>
              ) : (
                phoneNumber || t('Unknown')
              )}
            </Truncated>

            {call.direction === CallIntelTypes.CallDirectionEnum.DIRECTION_OUTBOUND && (
              <Icon name='phone-outgoing-small' color='light' />
            )}
          </div>
          <Text size='small' color='subdued'>
            {formatDate(call.startTime, DISPLAY_DATE_FORMAT)}
          </Text>
        </div>
        {showLocationChip && !!locationName && (
          <div css={styles.locationInfo}>
            <Chip.Location variant='default' maxWidth={'136px'}>
              {locationName}
            </Chip.Location>
          </div>
        )}
      </div>
      <div css={styles.action}>
        <NakedButton
          onClick={() =>
            openCallTakeawayPanel({
              channelId: call.id,
              locationId: call.locationId,
              callIntelData: call as Call,
              includeCallIntelligence: false,
            })
          }
          trackingId='call-intel-unhappy-widget-call-insight-button'
        >
          <Icon name='document-filled' color='light' />
        </NakedButton>
      </div>
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(1)};
    flex: 1;
    width: 100%;
    background-color: ${theme.colors.neutral5};
    border-radius: ${theme.borderRadius.small};
    min-width: min(320px, 100%);
  `,
  loaderContainer: css`
    display: flex;
    flex-direction: column;
  `,
  callItem: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
    border-radius: ${theme.borderRadius.small};
    padding: ${theme.spacing(0.75, 2)};
    background-color: ${theme.colors.white};
    box-shadow: ${theme.shadows.light};
    margin-bottom: ${theme.spacing(1)};
  `,

  callItemContent: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    flex: 1;
    @container (max-width: ${breakpoints.small.min}px) {
      flex-direction: column;
      justify-content: center;
      gap: ${theme.spacing(0.5)};
      align-items: flex-start;
    }
  `,
  userInfoContent: css`
    display: flex;
    flex-direction: column;
    white-space: nowrap;
    flex: 1;
  `,
  userInfoTitle: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    max-width: 124px;
    @container (max-width: ${breakpoints.small.min}px) {
      max-width: 150px;
    }
  `,
  locationInfo: css`
    flex: 1;
  `,
  action: css`
    flex-shrink: 0;
  `,
};
